import React, {useRef, useState} from 'react';
import '../../polyfill/urlsearchparams.es5';
import {ArticleOverviewStaticProvider} from '../ArticleOverviewStatic.jsx';
import Loadingbar from './Loader.jsx';
import {createUrlFromCurrentUrl} from '../ArticleOverviewHelper.js';
import ArticleOverviewContent from './ArticleOverviewContent.jsx';
import {
    clearSizeFilter,
    getShopGroup,
} from '../../utils/size-filter-persistence.js';
import {useManualScrollRestoration} from '@shopping-funnel/scripts/hooks/use-scroll-to-anchor.ts';

export default function ArticleOverview({
    givenArticleContent,
    givenFilterContent,
    givenHeaderContent,
    givenNavigationContent,
    givenLandmarkContent,
    givenConfigurationContent,
    givenShopContent,
    givenSearchResultContent,
    givenSeoContent,
    givenResponseLimitations,
    givenRecommendationContent,
    pointOfSaleId,
    givenTeaserContent,
    givenSanitizedUrl,
    givenRankingTrackingValue,
    givenResponseMetadata,
    completeModel
}) {
    const sizeFilterPersistenceEnabled = givenConfigurationContent?.sizeFilterPersistenceEnabled;
    const shopId = givenShopContent?.id;
    const filterRootShopId = givenShopContent?.filterRootShopId;

    const hasFilterParams = (url) => {
        for (const key of url.searchParams.keys()) {
            if (key.startsWith('fh_')) return true;
        }
        return false;
    };

    const stripFilterParams = (url) => {
        const keysToDelete = [];
        for (const key of url.searchParams.keys()) {
            if (key.startsWith('fh_')) keysToDelete.push(key);
        }
        keysToDelete.forEach(key => url.searchParams.delete(key));
        return url;
    };

    // If hits === 0 and there are filter params, redirect to clean URL
    if (typeof window !== 'undefined' && givenFilterContent?.hits === 0) {
        const currentUrl = new URL(window.location.href);
        if (hasFilterParams(currentUrl)) {
            // Clear stored size filter for this group since it resulted in no hits
            if (sizeFilterPersistenceEnabled) {
                const group = getShopGroup(shopId, filterRootShopId);
                if (group) {
                    clearSizeFilter(group);
                }
            }
            sessionStorage.setItem('filterRemovedNotification', 'true');
            const cleanUrl = stripFilterParams(currentUrl);
            window.location.replace(cleanUrl.toString());
            return null;
        }
    }

    const [articleContent] = useState(givenArticleContent);
    const [filterContent] = useState(givenFilterContent);
    const [overviewContent] = useState({
        navigationContent: givenNavigationContent,
        headerContent: givenHeaderContent,
        landmarkContent: givenLandmarkContent,
        shopContent: givenShopContent,
        searchResultContent: givenSearchResultContent,
        seoContent: givenSeoContent,
        responseLimitations: givenResponseLimitations,
        recommendationContent: givenRecommendationContent,
        leftNavigation: completeModel?.leftNavigation,
        rankingTrackingValue: givenRankingTrackingValue,
        responseMetadata: givenResponseMetadata,
    });

    const shopContent = overviewContent.shopContent;
    const searchContext = !shopContent?.id;
    const initialUrl = createInitialUrl();

    const searchParamsRef = useRef(initialUrl.searchParams);
    const categoryPathRef = useRef(shopContent?.prettyUrl);
    const filterRootShopIdRef = useRef(shopContent?.filterRootShopId);
    const prettyUrlRef = useRef(shopContent?.prettyUrl);
    const rootCategoryPathRef = useRef(shopContent?.rootCategoryPath);
    const shopIdRef = useRef(shopContent?.id);
    const urlPathRef = useRef(initialUrl.pathname);
    const shouldScrollUpAfterLoadRef = useRef(false)
    const imagePresentationTypeIdRef = useRef(givenConfigurationContent.imagePresentationTypeId);

    useManualScrollRestoration();

    return (
        <>
            <Loadingbar />
            <ArticleOverviewStaticProvider
                configurationContent={givenConfigurationContent}
                searchParams={searchParamsRef}
                categoryPath={categoryPathRef}
                filterRootShopId={filterRootShopIdRef}
                prettyUrl={prettyUrlRef}
                rootCategoryPath={rootCategoryPathRef}
                shopId={shopIdRef}
                urlPath={urlPathRef}
                shouldScrollUpAfterLoad={shouldScrollUpAfterLoadRef}
                imagePresentationTypeId={imagePresentationTypeIdRef}
                givenResponseMetadata={givenResponseMetadata}
            >
                <ArticleOverviewContent
                    givenArticleContent={articleContent}
                    givenShopContent={shopContent}
                    givenRecommendationContent={overviewContent.recommendationContent}
                    givenFilterContent={filterContent}
                    givenLandmarkContent={overviewContent.landmarkContent}
                    givenNavigationContent={overviewContent.navigationContent}
                    givenHeaderContent={overviewContent.headerContent}
                    pointOfSaleId={pointOfSaleId}
                    givenResponseLimitations={overviewContent.responseLimitations}
                    givenSearchResultContent={overviewContent.searchResultContent}
                    givenSeoContent={overviewContent.seoContent}
                    givenTeaserContent={givenTeaserContent}
                    givenLeftNavigation={overviewContent.leftNavigation}
                    givenSearchContext={searchContext}
                    givenSanitizedUrl={givenSanitizedUrl}
                    givenConfErrorCase={givenConfigurationContent.errorCase}
                    givenRankingTrackingValue={overviewContent.rankingTrackingValue}
                    completeModel={completeModel}
                />
            </ArticleOverviewStaticProvider>
        </>
    );

    function createInitialUrl() {
        if (givenSanitizedUrl) {
            const currentUrl = new URL(window.location.href);
            const url = currentUrl.origin + givenSanitizedUrl;
            const calculatedUrl = createUrlFromCurrentUrl(searchContext, url);

            history.replaceState({}, '', currentUrl);
            return calculatedUrl;
        } else {
            return createUrlFromCurrentUrl(searchContext);
        }
    }
}
